// Google OAuth Service for TherapyLink CRM
// Handles Google Identity Services (GIS) token flow for Gmail API access

const CLIENT_ID = '1071734912550-9mlfaajho2hhm6anmsh77h5tnura5e3u.apps.googleusercontent.com';
const SCOPES = 'https://www.googleapis.com/auth/gmail.compose https://www.googleapis.com/auth/gmail.readonly';

let tokenClient = null;
let accessToken = null;
let tokenExpiry = null;
let onAuthChange = null; // callback for UI updates

// Initialize the Google Identity Services token client
export function initGoogleAuth(authChangeCallback) {
  onAuthChange = authChangeCallback;
  
  // Check for saved token
  const saved = localStorage.getItem('gauth_token');
  const savedExpiry = localStorage.getItem('gauth_expiry');
  if (saved && savedExpiry && Date.now() < parseInt(savedExpiry)) {
    accessToken = saved;
    tokenExpiry = parseInt(savedExpiry);
    onAuthChange?.({ signedIn: true, email: localStorage.getItem('gauth_email') || '' });
  }

  // Wait for GIS script to load
  const interval = setInterval(() => {
    if (window.google?.accounts?.oauth2) {
      clearInterval(interval);
      tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPES,
        callback: handleTokenResponse,
        error_callback: (err) => {
          console.error('Google Auth error:', err);
          onAuthChange?.({ signedIn: false, error: err.message || 'Auth failed' });
        },
      });
    }
  }, 100);

  // Timeout after 10s
  setTimeout(() => clearInterval(interval), 10000);
}

function handleTokenResponse(response) {
  if (response.error) {
    console.error('Token error:', response.error);
    onAuthChange?.({ signedIn: false, error: response.error });
    return;
  }
  accessToken = response.access_token;
  // Token typically lasts 3600 seconds
  tokenExpiry = Date.now() + (response.expires_in || 3600) * 1000;
  
  localStorage.setItem('gauth_token', accessToken);
  localStorage.setItem('gauth_expiry', tokenExpiry.toString());

  // Fetch user email for display
  fetch('https://www.googleapis.com/gmail/v1/users/me/profile', {
    headers: { Authorization: `Bearer ${accessToken}` }
  })
    .then(r => r.json())
    .then(profile => {
      localStorage.setItem('gauth_email', profile.emailAddress || '');
      onAuthChange?.({ signedIn: true, email: profile.emailAddress || '' });
    })
    .catch(() => {
      onAuthChange?.({ signedIn: true, email: '' });
    });
}

// Trigger Google sign-in popup
export function signIn() {
  if (!tokenClient) {
    console.error('Google Auth not initialized yet');
    return;
  }
  // If we have a token but it might be expired, request with prompt:''
  if (accessToken) {
    tokenClient.requestAccessToken({ prompt: '' });
  } else {
    tokenClient.requestAccessToken({ prompt: 'consent' });
  }
}

// Sign out — revoke token and clear storage
export function signOut() {
  if (accessToken) {
    window.google?.accounts?.oauth2?.revoke(accessToken);
  }
  accessToken = null;
  tokenExpiry = null;
  localStorage.removeItem('gauth_token');
  localStorage.removeItem('gauth_expiry');
  localStorage.removeItem('gauth_email');
  onAuthChange?.({ signedIn: false });
}

// Get a valid access token, auto-refreshing if expired
export async function getAccessToken() {
  if (accessToken && tokenExpiry && Date.now() < tokenExpiry - 60000) {
    return accessToken;
  }
  
  // Token expired or missing — need to re-auth
  return new Promise((resolve, reject) => {
    if (!tokenClient) {
      reject(new Error('Google Auth not initialized'));
      return;
    }
    
    const origCallback = tokenClient.callback;
    tokenClient.callback = (response) => {
      handleTokenResponse(response);
      if (response.error) {
        reject(new Error(response.error));
      } else {
        resolve(response.access_token);
      }
    };
    
    tokenClient.requestAccessToken({ prompt: '' });
  });
}

// Check if user is currently signed in with a valid token
export function isSignedIn() {
  return !!(accessToken && tokenExpiry && Date.now() < tokenExpiry);
}

export function getAuthEmail() {
  return localStorage.getItem('gauth_email') || '';
}
